import axios from 'axios'

import { env } from '@maxus/website/constants'

import {
  clearTokens,
  decodeTokenPayload,
  getRefreshToken,
  setAccessToken,
} from './access-token'

/**
 * 无感刷新：access token 过期（或即将过期）时，用 refresh token 静默换取新的 access token，
 * 避免把用户踢回登录页。统一入口是 getValidAccessToken()，在发请求前（tRPC getHeaders /
 * axios 请求拦截器）以及 useAuth 判定登录态时调用。
 *
 * 服务端 POST /auth/refresh 只签发新的 access token，不轮换 refresh token，
 * 因此这里只更新 access token。
 */

// access token 剩余寿命低于此阈值即视为需要刷新，预留网络往返与时钟偏移余量。
const REFRESH_SKEW_MS = 60 * 1000

interface RefreshResponse {
  access_token: string
  token_type: string
  expires_in: number
}

// 单飞：并发调用共享同一次刷新，避免同时打出多个 /auth/refresh。
let inflight: Promise<string | null> | null = null

const expiresAt = (token: string): number | null => {
  const payload = decodeTokenPayload(token)
  return payload ? payload.exp * 1000 : null
}

export const isExpired = (token: string): boolean => {
  const at = expiresAt(token)
  return at === null || at <= Date.now()
}

export const isExpiringSoon = (token: string): boolean => {
  const at = expiresAt(token)
  return at === null || at - Date.now() <= REFRESH_SKEW_MS
}

const doRefresh = async (): Promise<string | null> => {
  const refreshToken = getRefreshToken()
  if (!refreshToken) return null

  try {
    const base = env.VITE_BASE_URL || 'http://localhost:3000'
    // 使用裸 axios（无拦截器）请求，避免走 httpClient 的请求拦截器再次触发刷新造成递归。
    const res = await axios.post<RefreshResponse>(
      `${base}/auth/refresh`,
      { refresh_token: refreshToken },
      { headers: { 'Content-Type': 'application/json' }, timeout: 30000 },
    )
    setAccessToken(res.data.access_token)
    return res.data.access_token
  } catch (err) {
    // 仅在服务端明确判定 refresh token 失效（401）时清空会话，交由上层跳登录；
    // 网络等临时故障保留令牌，等待下次重试，避免抖动网络把用户登出。
    if (axios.isAxiosError(err) && err.response?.status === 401) {
      clearTokens()
    }
    return null
  }
}

// 触发一次刷新（单飞）：成功返回新 access token，失败返回 null。
export const refreshAccessToken = (): Promise<string | null> => {
  if (!inflight) {
    inflight = doRefresh().finally(() => {
      inflight = null
    })
  }
  return inflight
}
